// Produkte — für jeden im Betrieb einsehbar.
//
// Anlegen und ändern darf nur, wer den Haken "Produkte anlegen" hat (oder Admin
// ist). Der Knopf verschwindet für alle anderen — aber das ist nur Höflichkeit:
// Durchgesetzt wird es in der Datenbank (Migration 029). Wer die API direkt
// anspricht, prallt genauso ab.

import { useMemo, useState } from "react";
import { useProducts, useUpsertProduct, type Product } from "@/lib/db";
import { useDarfIch } from "@/lib/rechte";
import { LogoLoader } from "@/components/LogoLoader";
import { Button, Card, EmptyState, Field, Input, Sheet, cn, euro } from "@/design-system/ui";
import { ModulHeader } from "@/modules/home/ModulHeader";

type Sortierung = "name" | "preis" | "kategorie";

/**
 * Der Hof verkauft ausschließlich landwirtschaftliche Erzeugnisse — Eier,
 * Kartoffeln, Gemüse, Obst, Gebäck, Geflügel. Alles der ermäßigte Satz.
 *
 * Steht als Konstante da und nicht als Eingabefeld, weil ein vertippter Satz
 * niemandem auffällt, bis eine falsche Rechnung beim Kunden liegt — und die holt
 * man nur per Storno und Neuausstellung zurück. Alle 21 Artikel stehen ohnehin
 * auf 7 %; das Feld konnte nur schaden.
 */
const MWST_SATZ = 7;

export default function ProduktePage() {
  const { data: produkte = [], isLoading } = useProducts();
  const darfAnlegen = useDarfIch("produkte.anlegen");

  const [suche, setSuche] = useState("");
  const [kategorie, setKategorie] = useState<string | null>(null);
  const [nurAktive, setNurAktive] = useState(true);
  const [sortierung, setSortierung] = useState<Sortierung>("kategorie");

  const [bearbeiten, setBearbeiten] = useState<Product | null>(null);
  const [neu, setNeu] = useState(false);

  // Die Kategorien kommen aus den Daten, nicht aus einer festen Liste — legt
  // jemand "Honig" an, taucht der Filter von selbst auf.
  const kategorien = useMemo(
    () => [...new Set(produkte.map((p) => p.category).filter(Boolean))].sort() as string[],
    [produkte]
  );

  const gefiltert = useMemo(() => {
    const s = suche.trim().toLowerCase();

    const liste = produkte.filter((p) => {
      if (nurAktive && !p.is_active) return false;
      if (kategorie && p.category !== kategorie) return false;
      if (!s) return true;
      return (
        p.name.toLowerCase().includes(s) ||
        (p.sku ?? "").toLowerCase().includes(s) ||
        (p.category ?? "").toLowerCase().includes(s)
      );
    });

    return [...liste].sort((a, b) => {
      if (sortierung === "preis") return a.price_cents - b.price_cents;
      if (sortierung === "name") return a.name.localeCompare(b.name, "de");
      // Nach Kategorie: innerhalb der Gruppe nach der gepflegten Reihenfolge.
      const k = (a.category ?? "").localeCompare(b.category ?? "", "de");
      return k !== 0 ? k : a.sort_order - b.sort_order;
    });
  }, [produkte, suche, kategorie, nurAktive, sortierung]);

  const ausgelistet = produkte.filter((p) => !p.is_active).length;

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col">
      <ModulHeader
        titel="Produkte"
        untertitel={
          isLoading
            ? "lädt …"
            : `${gefiltert.length} von ${produkte.length}${ausgelistet ? ` · ${ausgelistet} ausgelistet` : ""}`
        }
      />

      {/* ── Filter */}
      <div className="shrink-0 border-b border-ink-700/70 px-4 py-3">
        <Input
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="Name, Artikelnummer oder Kategorie …"
          autoComplete="off"
        />

        <div className="-mx-4 mt-3 flex gap-1.5 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Chip aktiv={!kategorie} onClick={() => setKategorie(null)}>
            Alle
          </Chip>
          {kategorien.map((k) => (
            <Chip key={k} aktiv={kategorie === k} onClick={() => setKategorie(k)}>
              {k}
            </Chip>
          ))}

          <span className="mx-1 w-px shrink-0 bg-ink-700" aria-hidden />

          <Chip aktiv={!nurAktive} onClick={() => setNurAktive((v) => !v)}>
            {nurAktive ? "Ausgelistete zeigen" : "Nur im Sortiment"}
          </Chip>
        </div>

        <div className="mt-3 flex items-center gap-1.5">
          <span className="text-xs text-ink-500">Sortiert nach</span>
          {(
            [
              ["kategorie", "Kategorie"],
              ["name", "Name"],
              ["preis", "Preis"],
            ] as [Sortierung, string][]
          ).map(([wert, label]) => (
            <button
              key={wert}
              onClick={() => setSortierung(wert)}
              aria-pressed={sortierung === wert}
              className={cn(
                "rounded-md px-2 py-1 text-xs font-medium transition-colors duration-fast",
                sortierung === wert
                  ? "bg-gruen-500/15 text-gruen-400"
                  : "text-ink-400 hover:bg-ink-800 hover:text-ink-200"
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Liste */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 pb-24">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <LogoLoader size="md" label="Produkte werden geladen" />
          </div>
        ) : gefiltert.length === 0 ? (
          <EmptyState
            title="Kein Produkt gefunden"
            hint={
              suche
                ? `Nichts passt zu „${suche}".`
                : nurAktive
                  ? "In dieser Kategorie ist nichts im Sortiment."
                  : "Es gibt noch keine Produkte."
            }
          />
        ) : (
          <ul className="space-y-2">
            {gefiltert.map((p) => (
              <li key={p.id}>
                <ProduktZeile
                  p={p}
                  antippbar={darfAnlegen}
                  onOeffnen={() => darfAnlegen && setBearbeiten(p)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Anlegen — nur mit Recht. Fehlt es, ist der Knopf nicht da; die Datenbank
          würde den Versuch ohnehin abweisen. */}
      {darfAnlegen && (
        <button
          onClick={() => setNeu(true)}
          aria-label="Produkt anlegen"
          className={cn(
            "fixed bottom-[max(5rem,calc(env(safe-area-inset-bottom)+5rem))] right-5 z-30",
            "flex h-14 w-14 items-center justify-center rounded-full",
            "bg-gruen-500 text-gruen-on shadow-lg shadow-black/20",
            "transition-transform duration-fast active:scale-95",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-400 focus-visible:ring-offset-2"
          )}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}

      {(bearbeiten || neu) && (
        <ProduktSheet
          p={bearbeiten}
          kategorien={kategorien}
          onClose={() => {
            setBearbeiten(null);
            setNeu(false);
          }}
        />
      )}
    </div>
  );
}

function Chip({
  aktiv,
  onClick,
  children,
}: {
  aktiv: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={aktiv}
      className={cn(
        "flex h-9 shrink-0 items-center rounded-full border px-4 text-sm font-medium",
        "transition-colors duration-fast",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
        aktiv
          ? "border-gruen-500/50 bg-gruen-500/15 text-gruen-400"
          : "border-ink-700 bg-ink-800/60 text-ink-300 hover:border-ink-600 hover:text-ink-100"
      )}
    >
      {children}
    </button>
  );
}

function ProduktZeile({
  p,
  antippbar,
  onOeffnen,
}: {
  p: Product;
  antippbar: boolean;
  onOeffnen: () => void;
}) {
  const inhalt = (
    <Card
      className={cn(
        "flex items-center justify-between gap-3 px-4 py-3",
        antippbar && "transition-colors duration-fast hover:border-ink-600",
        !p.is_active && "opacity-60"
      )}
    >
      <div className="min-w-0">
        <div
          className={cn(
            "truncate text-[15px] font-medium",
            p.is_active ? "text-ink-100" : "text-ink-400 line-through"
          )}
        >
          {p.name}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-500">
          {p.sku && <span className="font-mono">{p.sku}</span>}
          {p.category && <span>{p.category}</span>}
          {!p.is_active && <span className="text-ink-400">ausgelistet</span>}
        </div>
      </div>

      <div className="shrink-0 text-right">
        <div className="text-[15px] font-semibold tabular-nums text-ink-50">
          {euro(p.price_cents)}
        </div>
        <div className="mt-0.5 text-xs tabular-nums text-ink-400">
          pro {einheit(p.unit)} · {p.vat_rate} %
        </div>
      </div>
    </Card>
  );

  // Wer nicht ändern darf, bekommt keinen Knopf, der nichts tut.
  return antippbar ? (
    <button onClick={onOeffnen} className="w-full text-left">
      {inhalt}
    </button>
  ) : (
    inhalt
  );
}

const einheit = (u: string) => (u === "kg" ? "kg" : u === "glas" ? "Glas" : "Stück");

/** Anlegen und Ändern im selben Blatt — der Unterschied ist nur, ob `p` da ist. */
function ProduktSheet({
  p,
  kategorien,
  onClose,
}: {
  p: Product | null;
  kategorien: string[];
  onClose: () => void;
}) {
  const speichern = useUpsertProduct();
  const neu = !p;

  const [name, setName] = useState(p?.name ?? "");
  const [kategorie, setKategorie] = useState(p?.category ?? "");
  const [einheitWahl, setEinheitWahl] = useState(p?.unit ?? "stueck");
  const [preis, setPreis] = useState(
    p ? (p.price_cents / 100).toFixed(2).replace(".", ",") : ""
  );
  const [aktiv, setAktiv] = useState(p?.is_active ?? true);
  const [fehler, setFehler] = useState<string | null>(null);

  const cents = Math.round(parseFloat(preis.replace(",", ".")) * 100);
  const gueltig = name.trim().length > 1 && Number.isFinite(cents) && cents >= 0;

  return (
    <Sheet
      open
      onClose={onClose}
      title={neu ? "Neues Produkt" : p!.name}
      footer={
        <div className="space-y-2">
          {fehler && (
            <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {fehler}
            </p>
          )}
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={!gueltig}
            loading={speichern.isPending}
            onClick={async () => {
              setFehler(null);
              try {
                await speichern.mutateAsync({
                  ...(p ? { id: p.id } : {}),
                  name: name.trim(),
                  category: kategorie.trim() || null,
                  unit: einheitWahl,
                  price_cents: cents,
                  vat_rate: MWST_SATZ,
                  is_active: aktiv,
                });
                onClose();
              } catch (e) {
                setFehler(fehlertext(e));
              }
            }}
          >
            {neu ? "Produkt anlegen" : "Speichern"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Eier braun XL"
            autoFocus={neu}
          />
        </Field>

        <Field label="Kategorie" optional hint="Neue Kategorien einfach eintippen">
          <Input
            value={kategorie}
            onChange={(e) => setKategorie(e.target.value)}
            placeholder="Eier, Kartoffeln, Nebenartikel …"
            list="kategorien"
          />
          <datalist id="kategorien">
            {kategorien.map((k) => (
              <option key={k} value={k} />
            ))}
          </datalist>
        </Field>

        <Field label="Verkaufseinheit">
          <div className="flex gap-1.5">
            {(
              [
                ["stueck", "Stück"],
                ["kg", "Kilo"],
                ["glas", "Glas"],
              ] as [string, string][]
            ).map(([wert, label]) => (
              <button
                key={wert}
                onClick={() => setEinheitWahl(wert as Product["unit"])}
                aria-pressed={einheitWahl === wert}
                className={cn(
                  "h-11 flex-1 rounded-lg border text-sm font-medium transition-colors duration-fast",
                  einheitWahl === wert
                    ? "border-gruen-500/50 bg-gruen-500/15 text-gruen-400"
                    : "border-ink-700 bg-ink-900 text-ink-300 hover:border-ink-600"
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </Field>

        <Field
          label={`Preis pro ${einheit(einheitWahl)}`}
          hint="Brutto — so, wie es der Kunde bezahlt. Die Steuer wird herausgerechnet."
        >
          <Input
            value={preis}
            onChange={(e) => setPreis(e.target.value)}
            inputMode="decimal"
            placeholder="0,32"
            className="tabular-nums text-lg"
          />
        </Field>

        {/* Kein Eingabefeld mehr, sondern eine Feststellung.
            Der Hof verkauft ausschließlich landwirtschaftliche Erzeugnisse — alles
            7 %. Als freies Feld war es eine offene Falltür: Ein vertippter Satz
            fällt niemandem auf, bis eine falsche Rechnung beim Kunden liegt, und
            die bekommt man nur per Storno und Neuausstellung zurück.
            Kommt je ein Artikel mit 19 % dazu (etwas, das kein Lebensmittel ist),
            wird hier wieder ein Feld daraus. */}
        <Field label="Mehrwertsteuer">
          <div className="flex items-center gap-3 rounded-lg border border-ink-700 bg-ink-900 px-4 py-3">
            <span className="text-lg font-semibold tabular-nums text-ink-100">7 %</span>
            <span className="text-sm text-ink-400">landwirtschaftliche Erzeugnisse</span>
          </div>
        </Field>

        {!neu && (
          <button
            onClick={() => setAktiv((v) => !v)}
            className={cn(
              "flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors duration-fast",
              aktiv ? "border-gruen-500/40 bg-gruen-500/10" : "border-ink-700 bg-ink-900"
            )}
          >
            <span
              className={cn(
                "grid h-6 w-6 shrink-0 place-items-center rounded-md border-2 text-xs font-bold",
                aktiv ? "border-gruen-500 bg-gruen-500 text-gruen-on" : "border-ink-600"
              )}
            >
              {aktiv && "✓"}
            </span>
            <span className="min-w-0">
              <span className="block text-[15px] text-ink-100">
                {aktiv ? "Wird verkauft" : "Nicht mehr im Sortiment"}
              </span>
              <span className="block text-sm text-ink-400">
                Ausgelistete Produkte bleiben auf alten Rechnungen stehen
              </span>
            </span>
          </button>
        )}

        {!neu && (
          <p className="text-sm text-ink-500">
            Ein geänderter Preis gilt ab sofort für neue Bestellungen. Bereits erzeugte
            Rechnungen bleiben unberührt.
          </p>
        )}
      </div>
    </Sheet>
  );
}

/** Die RLS meldet sich mit einer Codenummer — das versteht niemand. */
function fehlertext(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e);
  if (/row-level security|42501|permission denied/i.test(m)) {
    return "Dafür fehlt dir die Berechtigung. Ein Admin kann sie im Team-Bereich freigeben.";
  }
  if (/duplicate key|unique/i.test(m)) return "Ein Produkt mit dieser Artikelnummer gibt es schon.";
  return m;
}
